import React from 'react'
import { FiArrowRight } from '@react-icons/all-files/fi/FiArrowRight'
import { FiCalendar } from '@react-icons/all-files/fi/FiCalendar'
import { FiCheckCircle } from '@react-icons/all-files/fi/FiCheckCircle'
import { FiHeadphones } from '@react-icons/all-files/fi/FiHeadphones'
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle'
import { FiMessageSquare } from '@react-icons/all-files/fi/FiMessageSquare'
import { FiShield } from '@react-icons/all-files/fi/FiShield'
import { FiUsers } from '@react-icons/all-files/fi/FiUsers'
import { FiFilm } from '@react-icons/all-files/fi/FiFilm'
import Action from '../primitives/Action'
import Container from '../foundations/Container'
import Media from '../primitives/Media'
import { MEDIA_POLICY_KEYS } from '../primitives/mediaPolicy'
import useRevealOnce from '../motion/useRevealOnce'
import motionStyles from '../motion/MotionReveal.module.scss'
import styles from './ExperienceProof.module.scss'

const signalIcons = {
  experience: FiShield,
  service: FiHeadphones,
  execution: FiCheckCircle,
}

const homepageSignalIcons = {
  experience: FiUsers,
  service: FiMessageSquare,
  execution: FiCalendar,
}

const ExperienceProof = ({
  content,
  image,
  imageAlt,
  onPrimaryAction,
  onProjectAction,
  onSecondaryAction,
  project,
  motionProfile = 'static',
}) => {
  const shouldAnimate = motionProfile === 'homepage'
  const { entered, ref } = useRevealOnce({ enabled: shouldAnimate })
  const activeSignalIcons = shouldAnimate ? homepageSignalIcons : signalIcons

  return (
    <section
      className={`${styles.section} ${shouldAnimate ? styles.homepage : ''}`.trim()}
      aria-labelledby='homepage-experience-proof-heading'
    >
      <Container
        className={shouldAnimate ? styles.homepageContainer : ''}
        width={shouldAnimate ? 'wide' : 'standard'}
      >
        <div className={styles.grid}>
          <div className={styles.copy}>
            {content?.subheading && <span className={styles.eyebrow}>{content.subheading}</span>}
            <h2 id='homepage-experience-proof-heading'>{content?.heading}</h2>
            {content?.body && <p className={styles.intro}>{content.body}</p>}

            <ul className={styles.signals}>
              {(content?.items || []).map(item => {
                const Icon = activeSignalIcons[item.key] || FiCheckCircle

                return (
                  <li key={item.key}>
                    <span className={styles.signalIcon} aria-hidden='true'><Icon /></span>
                    <span>
                      <strong>{item.title}</strong>
                      <small>{item.body}</small>
                    </span>
                  </li>
                )
              })}
            </ul>

            <div className={styles.actions}>
              <Action
                className={shouldAnimate ? styles.primaryAction : ''}
                icon={shouldAnimate ? FiHeadphones : FiMessageCircle}
                onClick={onPrimaryAction}
                size='large'
                variant='primary'
                wrapContent={!shouldAnimate}
              >
                {shouldAnimate ? (
                  <>
                    <span className={styles.actionLabel}>{content?.cta?.label}</span>
                    <FiArrowRight aria-hidden='true' />
                  </>
                ) : content?.cta?.label}
              </Action>
              <Action
                className={shouldAnimate ? styles.secondaryAction : ''}
                href={content?.settings?.secondaryCta?.url}
                icon={shouldAnimate ? null : FiArrowRight}
                onClick={onSecondaryAction}
                size='large'
                variant='secondary'
                wrapContent={!shouldAnimate}
              >
                {shouldAnimate ? (
                  <>
                    <span className={styles.actionLabel}>{content?.settings?.secondaryCta?.label}</span>
                    <FiArrowRight aria-hidden='true' />
                  </>
                ) : content?.settings?.secondaryCta?.label}
              </Action>
            </div>
          </div>

          <div
            ref={ref}
            className={`${styles.visual} ${shouldAnimate ? motionStyles.reveal : ''} ${entered ? motionStyles.entered : ''}`.trim()}
          >
            <Media
              picture={image?.picture}
              src={image?.src || image}
              alt={imageAlt}
              policy={MEDIA_POLICY_KEYS.SECTION_SPLIT}
              ratio='16 / 10'
              className={styles.media}
            />

            <div className={styles.projectBar}>
              <div className={styles.projectSummary}>
                <FiFilm aria-hidden='true' />
                <span>
                  <small>{project?.eyebrow || 'Project spotlight'}</small>
                  <strong>{project?.title}</strong>
                  <p>{project?.meta}</p>
                </span>
              </div>
              <Action
                className={styles.projectAction}
                href={project?.url}
                icon={FiArrowRight}
                onClick={onProjectAction}
                size='compact'
                variant='quiet'
              >
                {project?.ctaLabel || 'View this production'}
              </Action>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

export default ExperienceProof
